import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  LinearProgress,
  Paper,
} from "@mui/material";
import color from "../../Constants/colors";
import Circle from "./Circle";
import { EvaluationStatus, Languages } from "../../Enums/companyStatus";
import CheckIcon from "@mui/icons-material/Check";
import RotatingCachedIcon from "./RotatingCachedIcon";
import {
  useEvaluationModel,
  usePauseEvaluation,
  useResumeEvaluation,
} from "../../Query/useEvaluation";
import {
  useCheckProgress,
  useEvaluationStatus,
} from "../../Query/useCompanies";
import { useUser } from "../../Query/useUsers";
import React, { useContext, useEffect, useState, useMemo } from "react";
import { evaluationModel } from "../../Models/evaluationModel";
import { useParams, useNavigate } from "react-router-dom";
import { SelectedCompanyContext } from "../../Contexts/SelectedCompanyContext";
import { useTranslation } from "react-i18next";
import { StopCircle, PlayCircle } from "@mui/icons-material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { getIconForEvalInProgress } from "../../Helpers/iconHelpers";
import { EvaluationProgress } from "../../Models/evaluation";
import { useTerminology } from "../../Core/useTerminology";
import { parseDateUTC, formatTime } from "../../Helpers/dateHelpers";

const EvaluationInProgress = () => {
  const [modelOptions, setModelOptions] = useState<evaluationModel[]>([]);
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { data: useEvaluationModelData } = useEvaluationModel();
  const { data: checkProgressData, isLoading: checkProgressLoading } =
    useCheckProgress();
  const { colors: workspace } = useWorkspaceColors();
  const companyId = checkProgressData?.company?.id ?? null;
  const { selectedCompanyId, setSelectedCompanyId } = useContext(
    SelectedCompanyContext,
  );
  const terminology = useTerminology();
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const { data: evaluationStatus } = useEvaluationStatus(evaluationId);
  const currentStatus = Number(evaluationStatus?.status);
  const [hasJustPaused, setHasJustPaused] = useState(false);
  const canResumeEvaluation = [
    EvaluationStatus.PAUSED,
    EvaluationStatus.ERROR,
    EvaluationStatus.TIME_LIMIT_EXCEEDED,
  ].includes(currentStatus);

  const [isStopping, setStopping] = useState(false);
  const [isPausing, setPausing] = useState(false);
  const [isResuming, setResuming] = useState(false);

  const { t } = useTranslation("evaluationPage");

  const pauseEvaluationMutation = usePauseEvaluation();
  const resumeEvaluationMutation = useResumeEvaluation();

  const currentEvaluation = useMemo(
    () =>
      checkProgressData?.evaluations?.find(
        (e) => e.id.toString() === evaluationId,
      ) || checkProgressData?.evaluation,
    [checkProgressData, evaluationId],
  );

  const evaluationStartTime = useMemo(
    () => evaluationStatus?.started_at || currentEvaluation?.created_at,
    [evaluationStatus?.started_at, currentEvaluation?.created_at],
  );

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [etaSeconds, setEtaSeconds] = useState<number | null>(null);
  const [estTimePerCat, setEstTimePerCat] = useState<number | null>(null);
  const [smoothProgress, setSmoothProgress] = useState(0);

  // Sync and Navigation Logic
  useEffect(() => {
    if (checkProgressLoading || !evaluationId) return;

    if (!selectedCompanyId && companyId) {
      setSelectedCompanyId(companyId);
    }

    if (useEvaluationModelData && user) {
      const accessibleIds = new Set(
        useEvaluationModelData.accessible_model_ids,
      );
      const accessibleModels = useEvaluationModelData.all_models.filter(
        (model) => accessibleIds.has(model.model_id),
      );
      setModelOptions(accessibleModels);
    }

    const status = Number(evaluationStatus?.status);

    if (status === EvaluationStatus.PAUSED) {
      return;
    }
    const shouldGoToEntityPage =
      selectedCompanyId &&
      (status === EvaluationStatus.EVALUATED ||
        status === EvaluationStatus.NEEDS_MORE_INFO ||
        (checkProgressData &&
          !checkProgressData.in_progress &&
          !currentEvaluation) ||
        (checkProgressData && !checkProgressData.company));

    if (status === EvaluationStatus.ERROR && !canResumeEvaluation) {
      navigate("/evaluation_error");
      return;
    }

    if (shouldGoToEntityPage) {
      navigate(`/entity/${selectedCompanyId}?edit_mode=true`);
    }
  }, [
    checkProgressLoading,
    evaluationId,
    selectedCompanyId,
    companyId,
    useEvaluationModelData,
    user,
    evaluationStatus?.status,
    checkProgressData,
    currentEvaluation,
    navigate,
    setSelectedCompanyId,
    hasJustPaused,
  ]);

  // Handle grading progress estimates
  useEffect(() => {
    const grading = evaluationStatus?.grading_progress;
    const resumedFrom = grading?.resumed_from_category_index ?? 0;
    const categoriesGradedThisSession =
      (grading?.current_category_index ?? 0) - resumedFrom;
    if (grading && categoriesGradedThisSession > 0) {
      const startTime = parseDateUTC(grading.grading_started_at);
      const now = Date.now();
      const elapsedInGrading = (now - startTime) / 1000;
      const actualAvg = elapsedInGrading / categoriesGradedThisSession;

      setEstTimePerCat((prev) =>
        prev === null ? actualAvg : prev * 0.8 + actualAvg * 0.2,
      );
    }
  }, [
    evaluationStatus?.grading_progress,
    evaluationStatus?.grading_progress?.current_category_index,
  ]);

  // UI Updates Timer
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      if (evaluationStartTime) {
        const start = parseDateUTC(evaluationStartTime);
        setElapsedSeconds(Math.max(0, Math.floor((now - start) / 1000)));
      }

      const grading = evaluationStatus?.grading_progress;
      if (grading) {
        const gradingStart = parseDateUTC(grading.grading_started_at);
        const elapsedInGrading = Math.floor((now - gradingStart) / 1000);
        const { current_category_index, total_categories } = grading;
        const resumedFrom = grading.resumed_from_category_index ?? 0;
        const categoriesInSession = current_category_index - resumedFrom;

        if (estTimePerCat !== null) {
          const remainingInSession =
            estTimePerCat * (total_categories - resumedFrom);
          setEtaSeconds(
            Math.max(0, Math.round(remainingInSession - elapsedInGrading)),
          );

          const timeSpentInCurrent = Math.max(
            0,
            elapsedInGrading - categoriesInSession * estTimePerCat,
          );
          const subProgress = Math.min(
            0.98,
            timeSpentInCurrent / estTimePerCat,
          );
          setSmoothProgress(
            ((current_category_index + subProgress) / total_categories) * 100,
          );
        } else {
          const initialEstimate = 40;
          setSmoothProgress(
            (Math.min(0.9, elapsedInGrading / initialEstimate) /
              total_categories) *
              100,
          );
          setEtaSeconds(null);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [evaluationStatus?.grading_progress, estTimePerCat, evaluationStartTime]);

  const evaluationProgress: EvaluationProgress[] = useMemo(
    () => [
      {
        id: EvaluationStatus.EVALUATION_IN_PROGRESS,
        name: t("evaluationInProgress.progress.inProgress"),
      },
      {
        id: EvaluationStatus.FETCHING_COMPANY_DETAILS,
        name: t("evaluationInProgress.progress.fetchingDetails"),
      },
      {
        id: EvaluationStatus.PROCESSING_COMPANY_INFORMATIONS,
        name: t("evaluationInProgress.progress.processing"),
      },
      {
        id: EvaluationStatus.GRADING_COMPANY,
        name: t("evaluationInProgress.progress.grading"),
      },
    ],
    [t],
  );

  const evaluationData = useMemo(() => {
    if (currentEvaluation) {
      return {
        entity: currentEvaluation.entity,
        language: currentEvaluation.language,
        model_id: currentEvaluation.model_id,
        question_list: currentEvaluation.question_list,
      };
    }
    if (checkProgressData?.evaluation) {
      return {
        entity: checkProgressData.evaluation.entity,
        language: checkProgressData.evaluation.language,
        model_id: checkProgressData.evaluation.model_id,
        question_list: checkProgressData.evaluation.question_list,
      };
    }
    if (checkProgressData?.company) {
      return {
        entity: { name: checkProgressData.company.name },
        language: undefined,
        model_id: undefined,
        question_list: undefined,
      };
    }
    return null;
  }, [currentEvaluation, checkProgressData]);

  const modelName = useMemo(() => {
    const modelId = evaluationData?.model_id;
    return modelOptions.find((m) => m.model_id === modelId)?.model_name;
  }, [evaluationData?.model_id, modelOptions]);

  const InfoSections = useMemo(
    () => [
      {
        key: "companyName",
        title: t("evaluationInProgress.infoSections.companyName", {
          entity: terminology.singular,
        }),
        value: evaluationData?.entity?.name || checkProgressData?.company?.name,
      },
      {
        key: "modelName",
        title: t("evaluationInProgress.infoSections.modelName"),
        value: modelName,
      },
      {
        key: "certificationCategory",
        title: t("evaluationInProgress.infoSections.certificationCategory"),
        value: evaluationData?.question_list?.category,
      },
      {
        key: "questionList",
        title: t("evaluationInProgress.infoSections.questionList"),
        value: evaluationData?.question_list?.name,
      },
      {
        key: "language",
        title: t("evaluationInProgress.infoSections.language"),
        value:
          evaluationData?.language !== undefined
            ? Languages[
                evaluationData.language as unknown as keyof typeof Languages
              ]
            : undefined,
      },
    ],
    [
      t,
      terminology.singular,
      evaluationData,
      checkProgressData?.company?.name,
      modelName,
    ],
  );

  const handlePauseEvaluation = (id: string) => {
    setPausing(true);
    setHasJustPaused(true);

    pauseEvaluationMutation.mutate(id, {
      onSuccess: () => {
        setPausing(false);
      },
      onError: () => {
        setPausing(false);
        setHasJustPaused(false);
      },
    });
  };

  const handleResumeEvaluation = (id: string) => {
    setResuming(true);

    resumeEvaluationMutation.mutate(id, {
      onSuccess: () => {
        setResuming(false);
      },
      onError: () => {
        setResuming(false);
      },
    });
  };

  if (checkProgressLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        height: "100%",
        overflow: "auto",
        backgroundColor: color.gray50,
        position: "relative",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 2, sm: 3, md: 4 },
          position: "relative",
          minHeight: "100%",
        }}
      >
        {/* Stop Evaluation Button - Floating in top right */}
        <Tooltip
          title={
            canResumeEvaluation
              ? t("evaluationInProgress.resumeTooltip")
              : t("evaluationInProgress.pauseTooltip")
          }
          arrow
        >
          <span>
            <IconButton
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color:
                  isPausing || isResuming
                    ? color.btn_dark_disabled
                    : workspace.accent,
                backgroundColor: color.white,
                border: `1px solid ${color.gray200}`,
                "&:hover": {
                  color: workspace.accent,
                  backgroundColor: color.gray100,
                  transform: "scale(1.05)",
                },
                borderRadius: "50%",
                p: 1.5,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                zIndex: 10,
                transition: "all 0.2s ease",
              }}
              onClick={() =>
                evaluationId &&
                (canResumeEvaluation
                  ? handleResumeEvaluation(evaluationId)
                  : handlePauseEvaluation(evaluationId))
              }
              disabled={isPausing || isResuming}
            >
              {canResumeEvaluation ? (
                <PlayCircle sx={{ fontSize: { xs: 22, sm: 28 } }} />
              ) : (
                <StopCircle sx={{ fontSize: { xs: 22, sm: 28 } }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* Navigation Arrows */}
        {checkProgressData?.evaluations &&
          checkProgressData.evaluations.length > 1 && (
            <>
              <IconButton
                onClick={() => {
                  const idx = checkProgressData.evaluations.findIndex(
                    (e) => e.id.toString() === evaluationId,
                  );
                  if (idx > 0)
                    navigate(
                      `/evaluation_in_progress/${
                        checkProgressData.evaluations[idx - 1].id
                      }`,
                    );
                }}
                disabled={
                  checkProgressData.evaluations.findIndex(
                    (e) => e.id.toString() === evaluationId,
                  ) === 0
                }
                sx={{
                  display: { xs: "none", md: "flex" },
                  position: "absolute",
                  top: "50%",
                  left: { md: -10, lg: -20 },
                  transform: "translateY(-50%)",
                  color: workspace.primary_dark,
                  backgroundColor: color.white,
                  border: `1px solid ${color.gray200}`,
                  "&:hover": {
                    backgroundColor: color.gray100,
                    color: workspace.accent,
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  borderRadius: "50%",
                  p: 1.5,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  zIndex: 10,
                }}
              >
                <ArrowBackIosNewIcon fontSize="large" sx={{ ml: -0.5 }} />
              </IconButton>
              <IconButton
                onClick={() => {
                  const idx = checkProgressData.evaluations.findIndex(
                    (e) => e.id.toString() === evaluationId,
                  );
                  if (idx < checkProgressData.evaluations.length - 1)
                    navigate(
                      `/evaluation_in_progress/${
                        checkProgressData.evaluations[idx + 1].id
                      }`,
                    );
                }}
                disabled={
                  checkProgressData.evaluations.findIndex(
                    (e) => e.id.toString() === evaluationId,
                  ) ===
                  checkProgressData.evaluations.length - 1
                }
                sx={{
                  display: { xs: "none", md: "flex" },
                  position: "absolute",
                  top: "50%",
                  right: { md: -10, lg: -20 },
                  transform: "translateY(-50%)",
                  color: workspace.primary_dark,
                  backgroundColor: color.white,
                  border: `1px solid ${color.gray200}`,
                  "&:hover": {
                    backgroundColor: color.gray100,
                    color: workspace.accent,
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  borderRadius: "50%",
                  p: 1.5,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  zIndex: 10,
                }}
              >
                <ArrowForwardIosIcon fontSize="large" />
              </IconButton>
            </>
          )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            backgroundColor: color.white,
          }}
        >
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            {evaluationStatus &&
              evaluationProgress.map((progress, index) => {
                const isLast = index === evaluationProgress.length - 1;
                const currentStatus =
                  evaluationStatus.status !== null &&
                  evaluationStatus.status !== undefined
                    ? Number(evaluationStatus.status)
                    : 0;
                const isCompleted = currentStatus > progress.id;
                const isCurrentStep = progress.id === currentStatus;
                const isGradingActive = !!(
                  currentStatus >= EvaluationStatus.GRADING_COMPANY &&
                  evaluationStatus.grading_progress &&
                  currentStatus === EvaluationStatus.GRADING_COMPANY
                );
                // Show loading only on the current active step
                const isPaused = currentStatus === EvaluationStatus.PAUSED;

                const isActive = isCurrentStep || isGradingActive || isPaused;

                return (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      md={3}
                      lg={2}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Circle
                        colored={progress.id <= currentStatus}
                        number={progress.id}
                        isActive={isActive}
                      >
                        {isCompleted ? (
                          <CheckIcon fontSize="large" />
                        ) : isActive ? (
                          <RotatingCachedIcon fontSize="large" />
                        ) : (
                          <Typography
                            variant="h5"
                            sx={{
                              color:
                                progress.id <= currentStatus
                                  ? color.white
                                  : workspace.primary_dark,
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </Typography>
                        )}
                      </Circle>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: workspace.primary_dark,
                          textAlign: "center",
                          mt: 1.5,
                          mb: 1,
                          minHeight: "3rem",
                          fontWeight: 500,
                        }}
                      >
                        {progress.name}
                      </Typography>
                    </Grid>
                    {!isLast && (
                      <Grid
                        item
                        xs="auto"
                        sx={{
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          mt: "-65px",
                          justifyContent: "center",
                          px: 1,
                        }}
                      >
                        <ArrowForwardIosIcon
                          sx={{
                            color:
                              progress.id < currentStatus
                                ? workspace.accent
                                : color.gray300,
                            fontSize: "1.5rem",
                            transition: "color 0.3s ease",
                          }}
                        />
                      </Grid>
                    )}
                  </React.Fragment>
                );
              })}
          </Grid>
        </Paper>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid
            item
            xs={12}
            md={evaluationStatus?.grading_progress ? 6 : 12}
            sx={{
              display: "flex",
              justifyContent: evaluationStatus?.grading_progress
                ? "flex-end"
                : "center",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: `1px solid ${color.gray200}`,
                borderLeft: `4px solid ${workspace.primary_dark}`,
                backgroundColor: color.white,
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                width: "100%",
                maxWidth: 600,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: workspace.primary_dark,
                  mb: 1,
                  fontWeight: 600,
                  fontSize: "1.1rem",
                }}
              >
                {t("evaluationInProgress.infoHeader")}
              </Typography>
              {InfoSections.map(
                (item, index) =>
                  item.value && (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        backgroundColor: color.gray50,
                        borderRadius: 1.5,
                        p: 1.5,
                        border: `1px solid ${color.gray200}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: workspace.accent,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: color.white,
                          flexShrink: 0,
                        }}
                      >
                        {getIconForEvalInProgress(item.key)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: color.gray600,
                            fontWeight: 500,
                            fontSize: "0.85rem",
                            mb: 0.25,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: workspace.primary_dark,
                            fontWeight: 500,
                            fontSize: "0.95rem",
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  ),
              )}
            </Paper>
          </Grid>
          {evaluationStatus?.grading_progress && (
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", justifyContent: "flex-start" }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${color.gray200}`,
                  borderLeft: `4px solid ${workspace.primary_dark}`,
                  backgroundColor: color.white,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: "100%",
                  maxWidth: 600,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: workspace.primary_dark,
                    mb: 1,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                  }}
                >
                  {t("evaluationInProgress.gradingProgress.header")}
                </Typography>
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 2,
                    p: 2,
                    backgroundColor: color.gray50,
                    borderRadius: 1.5,
                    border: `1px solid ${color.gray200}`,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: color.gray600,
                      fontWeight: 500,
                      fontSize: "0.85rem",
                      mb: 0.5,
                    }}
                  >
                    {t("evaluationInProgress.gradingProgress.categoryLabel")}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: workspace.accent,
                      fontWeight: 700,
                    }}
                  >
                    {evaluationStatus.grading_progress.current_category_index +
                      1}{" "}
                    / {evaluationStatus.grading_progress.total_categories}
                  </Typography>
                </Box>
                <Box sx={{ width: "100%", mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={smoothProgress}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: color.gray200,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: workspace.accent,
                        transition: "transform 1s linear",
                        borderRadius: 6,
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: workspace.primary_dark,
                      textAlign: "right",
                      mt: 0.5,
                      fontWeight: 500,
                    }}
                  >
                    {Math.round(smoothProgress)}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    gap: { xs: 1.5, sm: 2 },
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      textAlign: "center",
                      flex: 1,
                      p: 1.5,
                      backgroundColor: color.gray50,
                      borderRadius: 1.5,
                      border: `1px solid ${color.gray200}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: color.gray600, display: "block", mb: 0.5 }}
                    >
                      {t("evaluationInProgress.gradingProgress.elapsedTime")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: workspace.primary_dark,
                        fontWeight: 600,
                      }}
                    >
                      {formatTime(elapsedSeconds)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      textAlign: "center",
                      flex: 1,
                      p: 1.5,
                      backgroundColor: color.gray50,
                      borderRadius: 1.5,
                      border: `1px solid ${color.gray200}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: color.gray600, display: "block", mb: 0.5 }}
                    >
                      {t(
                        "evaluationInProgress.gradingProgress.estimatedRemaining",
                      )}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: workspace.accent,
                        fontWeight: 600,
                      }}
                    >
                      {etaSeconds !== null
                        ? formatTime(etaSeconds)
                        : t("evaluationInProgress.gradingProgress.calculating")}
                    </Typography>
                  </Box>
                </Box>
                {evaluationStatus.grading_progress.current_category_name &&
                  evaluationStatus.grading_progress.current_category_name.trim() !==
                    "" && (
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: color.gray50,
                        borderRadius: 1.5,
                        border: `1px solid ${color.gray200}`,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: workspace.primary_dark,
                          fontStyle: "italic",
                          fontWeight: 500,
                        }}
                      >
                        {
                          evaluationStatus.grading_progress
                            .current_category_name
                        }
                      </Typography>
                    </Box>
                  )}
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Evaluation dots indicator */}
        {checkProgressData?.evaluations &&
          checkProgressData.evaluations.length > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mt: 4,
                p: 1.5,
                backgroundColor: color.white,
                borderRadius: 2,
                border: `1px solid ${color.gray200}`,
                maxWidth: "fit-content",
                mx: "auto",
              }}
            >
              {checkProgressData.evaluations.map((evalItem, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor:
                      evalItem.id.toString() === evaluationId
                        ? workspace.accent
                        : color.gray300,
                    border: `1px solid ${color.gray200}`,
                    transition: "all 0.2s ease",
                  }}
                />
              ))}
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default EvaluationInProgress;
